import { i18n } from "@tellescope/i18n";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Dispatch, SetStateAction } from "react";
import { createStore } from "@tellescope/store";

/** URL viewId used by `/settings/phone/logs/views/:viewId` for the unfiltered all-logs view. */
export const ALL_PHONE_LOGS_VIEW_ID = "all";

const getDefaultTitle = () => i18n.t("Default View", { ns: "settings" });

export const DEFAULT_PHONE_LOGS_VIEW = {
  id: "default",
  businessId: "",
  updatedAt: new Date(0),
  creator: "",
  title: getDefaultTitle(),
  page: "PhoneLogs",
  columns: [
    { field: "Date" },
    { field: "From" },
    { field: "To" },
    { field: "Direction" },
    { field: "Status" },
    { field: "Type" },
  ],
  createdAt: new Date(0),
} as const satisfies TellescopeModelFromName<"table_views">;

interface PhoneLogsDefaultViewStore {
  defaultViewByUserId: Record<string, TellescopeModelFromName<"table_views">>;
  setUserDefaultView: (
    userId: string,
    newVal: Parameters<Dispatch<SetStateAction<TellescopeModelFromName<"table_views">>>>[0]
  ) => void;
  /** URL `:viewId` of the last `/settings/phone/logs/views/:viewId` the user visited. */
  lastSelectedViewId: string;
  setLastSelectedViewId: (viewId: string) => void;
}

// Intentionally NOT persisted: default-view filter/column changes are treated as an ephemeral draft
// (calendar-style). They drive the live table + the "Save as View" prompt, but reset to the pristine
// default on reload — the user persists them only by saving a named view.
export const { useStore: usePhoneLogsDefaultViewStore, useShallowStore: usePhoneLogsDefaultViewShallowStore } =
  createStore<PhoneLogsDefaultViewStore>((set) => ({
    defaultViewByUserId: {},
    setUserDefaultView: (userId, newVal) =>
      set((state) => {
        const currentValue = state.defaultViewByUserId[userId] ?? DEFAULT_PHONE_LOGS_VIEW;
        const resolvedVal = typeof newVal === "function" ? newVal(currentValue) : newVal;

        return {
          ...state,
          defaultViewByUserId: {
            ...state.defaultViewByUserId,
            [userId]: resolvedVal,
          },
        };
      }),
    lastSelectedViewId: ALL_PHONE_LOGS_VIEW_ID,
    setLastSelectedViewId: (viewId) => set({ lastSelectedViewId: viewId }),
  }));
